import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import UserBadge from "@/components/UserBadge";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import {
  adminBootstrap, adminStats, adminListUsers, adminUpdateUser,
  adminListPosts, adminDeletePost, adminListVideos, adminDeleteVideo, adminAudit,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Crown, Shield, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "users", label: "Users" },
  { id: "posts", label: "Posts" },
  { id: "videos", label: "Videos" },
  { id: "audit", label: "Audit Log" },
];

function UserManager() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (query = q) => {
    setLoading(true);
    adminListUsers(query).then(setUsers).finally(() => setLoading(false));
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);

  const patch = async (uid, fields) => {
    await adminUpdateUser(uid, fields);
    toast.success("Updated");
    load();
  };

  return (
    <div data-testid="admin-users">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="mb-4 flex gap-2 items-center glass rounded-md p-2">
        <Search className="h-4 w-4 text-amber ml-2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…"
          className="flex-1 bg-transparent outline-none text-zinc-200 placeholder:text-zinc-600 font-body px-2 py-1"
          data-testid="admin-user-search" />
        <Button size="sm" className="bg-amber hover:bg-amber-hover text-obsidian font-heading">Search</Button>
      </form>
      {loading ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber" /></div> : (
        <ul className="space-y-2">
          {users.map((u) => (
            <li key={u.user_id} className="flex items-center gap-3 border border-white/10 rounded-md p-3 bg-obsidian-surface/60" data-testid={`admin-user-${u.user_id}`}>
              {u.picture ? (
                <img src={u.picture} alt="" className="h-10 w-10 rounded-full object-cover border border-white/10" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-amber/20 text-amber font-heading flex items-center justify-center">{u.name?.[0] || "?"}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-heading text-white">{u.name || "—"}</div>
                  <UserBadge user={u} />
                  {u.ban_status && u.ban_status !== "active" && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">{u.ban_status}</span>
                  )}
                </div>
                <div className="text-xs text-zinc-500">{u.email}</div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Button size="sm" variant="outline" className="text-amber border-amber/30 hover:bg-amber/10" onClick={() => patch(u.user_id, { is_critic: !u.is_critic })} data-testid={`btn-toggle-critic-${u.user_id}`}>
                  {u.is_critic ? "Unverify critic" : "Make critic"}
                </Button>
                <Button size="sm" variant="outline" className="text-sky-300 border-sky-500/30 hover:bg-sky-500/10" onClick={() => patch(u.user_id, { is_verified: !u.is_verified })} data-testid={`btn-toggle-verified-${u.user_id}`}>
                  {u.is_verified ? "Unverify" : "Verify"}
                </Button>
                <Button size="sm" variant="outline" className="text-yellow-300 border-yellow-500/30 hover:bg-yellow-500/10" onClick={() => patch(u.user_id, { ban_status: u.ban_status === "suspended" ? "active" : "suspended" })} data-testid={`btn-toggle-suspend-${u.user_id}`}>
                  {u.ban_status === "suspended" ? "Unsuspend" : "Suspend"}
                </Button>
                <Button size="sm" variant="outline" className="text-red-300 border-red-500/30 hover:bg-red-500/10" onClick={() => patch(u.user_id, { ban_status: u.ban_status === "banned" ? "active" : "banned" })} data-testid={`btn-toggle-ban-${u.user_id}`}>
                  {u.ban_status === "banned" ? "Unban" : "Ban"}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContentManager({ kind }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const fn = kind === "posts" ? adminListPosts : adminListVideos;
    fn().then(setItems).finally(() => setLoading(false));
  };
  useEffect(load, [kind]);

  const remove = async (id) => {
    const fn = kind === "posts" ? adminDeletePost : adminDeleteVideo;
    await fn(id); toast.success("Deleted"); load();
  };

  return (
    <div data-testid={`admin-${kind}`}>
      {loading ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber" /></div> : (
        <ul className="space-y-2">
          {items.length === 0 && <li className="text-zinc-500 italic font-body">No {kind}.</li>}
          {items.map((it) => (
            <li key={it.id} className="flex items-start gap-3 border border-white/10 rounded-md p-3 bg-obsidian-surface/60" data-testid={`admin-${kind}-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-500 font-heading">{it.user_name} · {new Date(it.created_at).toLocaleString()} · on {it.title_id}</div>
                <div className="mt-1 text-sm text-zinc-200 font-body break-words">
                  {kind === "posts" ? it.body : (it.video_title || it.critic_name || it.url)}
                </div>
              </div>
              <button onClick={() => remove(it.id)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`admin-delete-${kind}-${it.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AuditLog() {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { adminAudit().then(setLog).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber" /></div>;
  return (
    <ul className="space-y-1.5 font-mono text-xs" data-testid="admin-audit-log">
      {log.length === 0 && <li className="text-zinc-500 italic font-body">No actions logged yet.</li>}
      {log.map((e) => (
        <li key={e.id} className="border border-white/10 rounded p-2 bg-obsidian-surface/40">
          <span className="text-amber">{e.action}</span>
          <span className="text-zinc-500"> by {e.actor_id} → {e.target}</span>
          <span className="text-zinc-600"> · {new Date(e.at).toLocaleString()}</span>
          {Object.keys(e.meta || {}).length > 0 && (
            <pre className="mt-1 text-zinc-500">{JSON.stringify(e.meta)}</pre>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function Admin() {
  const { user, loading: authLoading, refresh } = useAuth() || {};
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user?.is_admin) adminStats().then(setStats).catch(() => {});
  }, [user]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>;

  if (!user) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <Crown className="h-10 w-10 text-amber mx-auto mb-4" />
          <h1 className="font-display text-4xl text-white">Admin Console</h1>
          <p className="text-zinc-400 font-body mt-2">Two ways to access:</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => window.location.assign("/admin/login")}
              className="bg-amber hover:bg-amber-hover text-obsidian font-heading shadow-[0_10px_40px_-12px_rgba(245,166,35,0.6)]"
              data-testid="admin-go-to-login"
            >
              <Shield className="h-4 w-4 mr-2" /> Sign in with admin password
            </Button>
            <Button
              variant="outline"
              onClick={startGoogleLogin}
              className="border-white/10 hover:border-amber/50 text-zinc-200 font-heading"
              data-testid="admin-google-login"
            >
              Sign in with Google
            </Button>
          </div>
          <p className="text-xs text-zinc-600 mt-6 font-heading">Google sign-in users must claim admin via bootstrap on first run.</p>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <Crown className="h-10 w-10 text-amber mx-auto mb-4" />
          <h1 className="font-display text-4xl text-white">Become admin</h1>
          <p className="text-zinc-400 font-body mt-2">If no admin exists yet, you can claim ownership of this instance.</p>
          <Button
            onClick={async () => {
              try { await adminBootstrap(); toast.success("You're now admin"); refresh?.(); }
              catch (e) { toast.error(e?.response?.data?.detail || "Admin already exists"); }
            }}
            className="mt-6 bg-amber hover:bg-amber-hover text-obsidian font-heading"
            data-testid="admin-bootstrap-btn"
          >
            Claim admin
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-14" data-testid="admin-page">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-2 flex items-center gap-1.5">
          <Shield className="h-3 w-3" /> Admin control
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-white mb-8">Manage MovioHub</h1>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
            {Object.entries(stats).map(([k, v]) => (
              <div key={k} className="border border-white/10 rounded-md p-3 bg-obsidian-surface/60">
                <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-heading">{k}</div>
                <div className="font-display text-2xl text-white">{v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-1 border-b border-white/10 mb-6" data-testid="admin-tabs">
          {TABS.map((T) => (
            <button
              key={T.id}
              onClick={() => setTab(T.id)}
              className={`px-4 py-2.5 font-heading text-sm transition border-b-2 -mb-px ${tab === T.id ? "text-amber border-amber" : "text-zinc-400 border-transparent hover:text-zinc-200"}`}
              data-testid={`admin-tab-${T.id}`}
            >
              {T.label}
            </button>
          ))}
        </div>

        {tab === "users" && <UserManager />}
        {tab === "posts" && <ContentManager kind="posts" />}
        {tab === "videos" && <ContentManager kind="videos" />}
        {tab === "audit" && <AuditLog />}
      </main>
    </div>
  );
}
